import { normalizeStoredWorkroomBoardAddress } from "./workroomStorage";
import * as Schema from "effect/Schema";
import { MusicCompositionId, nativePreviewRunId } from "@cleo/contracts";
/**
 * The BoardDeck: address-scoped state for the deck of workbench
 * surfaces (§6 — this furniture must NOT wear the register's "Canvas", which
 * names the composition frame arranging Blueprint compiles over a World
 * slice; pw-0092-inv-01).
 *
 * This is intentionally a shallow view model: it owns an ordered set of
 * surface tabs and the fronted tab, while each feature continues to own its
 * durable resource state. Looking, opening, and fronting write no work state.
 * Browser surfaces point at preview tab ids, terminal surfaces point at
 * terminal session ids, file surfaces point at workspace paths, and the
 * agent/files/agents views remain singleton surfaces.
 *
 * The persisted key keeps its legacy right-panel name so existing installs
 * retain their open views. It is a compatibility address, not a second
 * surface model.
 */
import { scopedThreadKey as scopedLocalThreadKey } from "@cleo/client-runtime/environment";
import type { CanvasDoorTarget, RoomMemberType, ScopedThreadRef } from "@cleo/contracts";
import { BOARD_KINDS, type BoardKind } from "@cleo/contracts";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { resolveStorage } from "./lib/storage";

export { BOARD_KINDS, type BoardKind };
const isMusicProjectId = Schema.is(MusicCompositionId);
const decodeMusicProjectId = Schema.decodeUnknownSync(MusicCompositionId);

/**
 * Kinds the World registry still names but this Desk no longer opens: the
 * pull-request board and list were removed with hosted source control, and the
 * Diff board with the Desk's own Git features. A persisted or World-held tab of
 * any of these kinds is dropped at hydrate.
 */
export type RetiredBoardKind = "pull-request" | "pull-requests" | "diff";
const RETIRED_BOARD_KINDS: ReadonlySet<string> = new Set<RetiredBoardKind>([
  "pull-request",
  "pull-requests",
  "diff",
]);
export function isRetiredBoardKind(kind: unknown): boolean {
  return typeof kind === "string" && RETIRED_BOARD_KINDS.has(kind);
}

/** The kinds a deck seats as ONE board: every kind but the targeted (and retired) ones. */
export type SingletonBoardKind = Exclude<
  BoardKind,
  "file" | "member" | "preview" | "terminal" | "sub-session" | RetiredBoardKind
>;
const TARGETED_BOARD_KINDS: ReadonlySet<string> = new Set([
  "file",
  "member",
  "preview",
  "terminal",
  "sub-session",
]);
export function isSingletonBoardKind(kind: BoardKind): kind is SingletonBoardKind {
  return !TARGETED_BOARD_KINDS.has(kind) && !isRetiredBoardKind(kind);
}

/**
 * W-0e42699e (#1382): the surface a deck shows, never written back. Its saved
 * active tab, unless this person may not open that kind here (Goals is
 * founder-only; a tab saved in another World). Then the deck's first tab they
 * may open, and with none, no surface: the deck's ordinary empty state, which
 * offers only the boards they may open. The saved tab stays saved and active,
 * and shows again where it is offered. Two cases keep the saved tab, so the
 * deck's refusal stays honest: the room's own board (a Music room whose Music
 * is withdrawn; there the board is the room), and a policy not yet read
 * (`permits` is null).
 */
export function shownDeckSurface(
  deck: Pick<ThreadBoardDeckState, "surfaces" | "activeSurfaceId">,
  permits: ((kind: BoardKind) => boolean) | null,
  roomOwnKind: BoardKind | null,
): Board | null {
  // A session seat is navigation into a child room, never this room's content.
  const content = deck.surfaces.filter((surface) => surface.kind !== "sub-session");
  const saved = content.find((surface) => surface.id === deck.activeSurfaceId) ?? null;
  if (permits === null || saved === null) return saved;
  if (permits(saved.kind) || saved.kind === roomOwnKind) return saved;
  return content.find((surface) => permits(surface.kind)) ?? null;
}

export type Board =
  | { id: "goals"; kind: "goals" }
  | { id: `browser:${string}`; kind: "preview"; resourceId: string }
  | { id: "browser:new"; kind: "preview"; resourceId: null }
  | {
      id: `terminal:${string}`;
      kind: "terminal";
      resourceId: string;
      terminalIds: string[];
      activeTerminalId: string;
      splitDirection?: "horizontal" | "vertical";
    }
  | { id: "files"; kind: "files" }
  | {
      id: `file:${string}`;
      kind: "file";
      relativePath: string;
      revealLine: number | null;
      revealRequestId: number;
    }
  | {
      /**
       * One room member opened as a deck tab (CB19). Singleton by member
       * id; `pinnedRevisionKey` is the resolve-head-then-pin law (D-CT3):
       * the face resolves the lineage head once and pins the exact
       * revision it saw — a later republish is typed staleness with an
       * explicit refresh, never silent mutation.
       */
      id: `member:${string}`;
      kind: "member";
      memberId: string;
      memberType: RoomMemberType;
      worldKey: string;
      label: string;
      pinnedRevisionKey: string | null;
      canvasRevision?: Pick<CanvasDoorTarget, "revisionId" | "sequence">;
    }
  | { id: "agents"; kind: "agents" }
  | {
      id: "settings";
      kind: "settings";
      section?: "/settings/provider-keys";
      connectionId?: string;
      intakeProvider?: "openai";
    }
  | { id: "usage"; kind: "usage" }
  | { id: "world-commerce"; kind: "world-commerce" }
  /** Can 73: the founders' Members room board, a SINGLETON per deck; it reads the World's members. */
  | { id: "world-members"; kind: "world-members" }
  /**
   * The expanded music player, a SINGLETON at the addressed room level and
   * never nested (SP3, principal ruling 2026-08-28): it enters only through
   * the chrome doors (footer strip expand, the apps-group door, Cleo) —
   * never the birth/dual + menus — and it is a VIEW onto the process-global
   * sidecar: closing the tab never touches playback, and a restored tab
   * simply re-subscribes.
   */
  | { id: "music"; kind: "music" }
  | { id: "games"; kind: "games" }
  /** The Mobile App workroom's body (MA10): the native iOS preview, a SINGLETON per deck. */
  | { id: "mobile-app"; kind: "mobile-app" }
  /** The composition editor, owned by its addressed Boomgik! session. */
  | { id: "boomgik"; kind: "boomgik" }
  | { id: `boomgik:${string}`; kind: "boomgik"; projectId: string; label: string }
  /**
   * The member's calendar, a SINGLETON per deck (TM14, the Music recipe
   * applied to time): it enters only through the chrome doors and is a VIEW
   * onto the World's own calendar rows through the loopback lane — closing
   * the tab touches no event, and a restored tab simply re-reads.
   */
  | { id: "calendar"; kind: "calendar" }
  /**
   * The Inbox, a SINGLETON per deck (CM4): the top-right Inbox door stops
   * leaving the app and opens this surface, which will grow to hold all
   * communication capabilities. v1 is the member's World conversations over
   * the loopback lane, with the cockpit email inbox one explicit labelled
   * external door away inside the surface.
   */
  | { id: "inbox"; kind: "inbox" }
  /**
   * The Commercial Operations workroom's body (SB2/SB4, register §35): a
   * SINGLETON per deck. Launchers derive its entry from the entitled-board
   * fold — the deck holds only the seat; entitlement stays the Grants'.
   */
  | {
      id: "commerce";
      kind: "commerce";
      section?: "ledger" | "prices" | "models" | "credits" | "openai";
      targetWorldKey?: string;
      provider?: string;
      model?: string;
    }
  | { id: "models"; kind: "models"; section?: "catalog" | "tiers" | "connections" }
  /**
   * The World Board (register §38, BOARD1; pw-1377-inv-01): the board over
   * the World's Goal tree, a SINGLETON per deck at the addressed room level
   * (the Music/Calendar idiom). A lens folded at read — looking writes
   * nothing; it is never a Canvas (pw-1376-inv-01).
   */
  | { id: "kanban"; kind: "kanban" }
  | { id: "workrooms"; kind: "workrooms" }
  // Step 3 M: the Components Workroom. Its body is O's (the founders' Workrooms screen as a Canvas); until then it shows
  // the waiting body, and a World offers it only after O is installed.
  | { id: "components"; kind: "components" }
  | { id: "designer"; kind: "designer" }
  | { id: "media"; kind: "media" }
  | { id: "websites"; kind: "websites" }
  | { id: "crm"; kind: "crm" }
  | { id: "delegation-gates"; kind: "delegation-gates" }
  /**
   * NPA3: Neucleos Platform Admin is the console for every platform register;
   * `section` is the register the tab was last on. A tab persisted as the
   * retired `charter` or `tools` kind is crosswalked to this board at hydrate
   * (v13 below), at that section.
   */
  | { id: "platform-admin"; kind: "platform-admin"; section?: PlatformAdminSection }
  | {
      /**
       * One nested session's SEAT in the deck's merged row (§8 one-bar room:
       * species merge in presentation, never in meaning). The seat carries
       * only ordering — the session's existence, engagement, and liveness
       * stay the World index's; the room reconciles presence at render, and
       * the deck's close verbs never touch these (a session closes only
       * through its own engagement ceremony).
       */
      id: `sub-session:${string}`;
      kind: "sub-session";
      sessionId: string;
    };

/**
 * The deck belongs either to one local execution thread or to one
 * authoritative World Session that has not selected an execution yet. The
 * latter is a presentation address only; it never invents a local thread or
 * claims an Artifact-backed World Canvas revision exists.
 */
/** The console's registers (NPA3), in rail order. */
export const PLATFORM_ADMIN_SECTIONS = [
  "overview",
  "instructions",
  "charter",
  "tools",
  "rooms",
] as const;
export type PlatformAdminSection = (typeof PLATFORM_ADMIN_SECTIONS)[number];

export type DeckScopeRef =
  | ScopedThreadRef
  | { readonly worldKey: string; readonly sessionId: string };

/** What a room may ask the deck to open by default: Goals, or a board's surface body. */
export type DeckDefaultSurfaceKind = SingletonBoardKind;

export function deckScopeKey(ref: DeckScopeRef): string {
  return "worldKey" in ref
    ? `world-session:${encodeURIComponent(ref.worldKey)}:${encodeURIComponent(ref.sessionId)}`
    : scopedLocalThreadKey(ref);
}

// Preserve the established mutation body while widening its lawful address.
const scopedThreadKey = deckScopeKey;

// DK1: one name family, no aliases — an alias is exactly how the next
// session re-mints "Canvas" for furniture. The persisted key below is a
// compatibility ADDRESS, not a name: existing installs keep their open views.
const RIGHT_PANEL_STORAGE_KEY = "cleo:board-deck:v1";
// v9 removed the "plan" surface kind (plans render inline in the transcript).
// v10 keys pull-request surfaces by reference instead of a singleton tab.
// v11 stops persisting the pull-request list's shared panel, so a restart opens the page fresh.
// v12 initializes the addressed chat's deck with its agent Surface once.
// v13 (NPA3, 2026-09-18) crosswalks the retired charter and tools tabs to Platform Admin.
// v14 preserves the registry tab while renaming its persisted address to Workrooms.
// v15 drops the retired pull-request tabs and the pull-request list's shared panel deck.
// v16 drops the retired Diff tabs.
const RIGHT_PANEL_STORAGE_VERSION = 16;

/** The retired pull-request list's shared panel deck: dropped at hydrate (v15). */
const isPullRequestsPanelKey = (threadKey: string) => threadKey.endsWith(":pull-requests-panel");

export interface ThreadBoardDeckState {
  isOpen: boolean;
  activeSurfaceId: string | null;
  surfaces: Board[];
  /** True after the chat applied the one-time default deck view. The stored
   * field name is a compatibility address (pre-DK1 installs wrote it). */
  canvasInitialized?: true;
}

interface WorkbenchDeckStoreState {
  byThreadKey: Record<string, ThreadBoardDeckState>;
  /**
   * The room's default surface, once per deck: Goals for a plain thread; a
   * board-born thread's own body (SB-ANTI-05, 2026-09-03) — passed by the room
   * from the thread's durable workroomKey. A board default is idempotent
   * and self-healing: a deck already initialized with Goals for a board
   * thread gains and fronts the board body the moment the room knows it.
   */
  /** `null`: the deck opens on its empty Canvas, with no tab (a plain room for someone who may not open Goals). */
  ensureDeckDefaultSurface: (
    ref: DeckScopeRef,
    defaultKind?: DeckDefaultSurfaceKind | null,
  ) => void;
  open: (
    ref: DeckScopeRef,
    kind: Exclude<BoardKind, "file" | "member" | "terminal" | "sub-session" | RetiredBoardKind>,
  ) => void;
  /**
   * SB3: front a workroom's body at birth and claim the deck's default —
   * the later ensureDeckDefaultSurface sees an initialized deck and leaves
   * the board fronted instead of the hardcoded Goals default. A plain birth
   * never calls this and keeps today's behavior byte-identical.
   */
  openBoardSurfaceAtBirth: (ref: DeckScopeRef, kind: SingletonBoardKind) => void;
  openBoomgikProject: (ref: DeckScopeRef, projectId: string, label: string) => void;
  updateBoomgikTitle: (ref: DeckScopeRef, boardId: string, label: string) => void;
  /** W-dbec6dae: a Canvas tab takes the title its face read, so it never shows the Canvas's id. */
  updateMemberLabel: (ref: DeckScopeRef, surfaceId: string, label: string) => void;
  openBrowser: (ref: DeckScopeRef, tabId: string | null) => void;
  /**
   * Seat one nested session in the merged row's order (append if unseated).
   * Never activates and never opens the deck: the seat is pure ordering.
   */
  seatSession: (ref: DeckScopeRef, sessionId: string) => void;
  openMember: (
    ref: DeckScopeRef,
    member: {
      readonly memberId: string;
      readonly memberType: RoomMemberType;
      readonly canvasRevision?: Pick<CanvasDoorTarget, "revisionId" | "sequence">;
      readonly worldKey: string;
      readonly label: string;
    },
  ) => void;
  pinMemberRevision: (ref: DeckScopeRef, surfaceId: string, revisionKey: string) => void;
  /** A Canvas tab opened at a saved revision goes back to following the head ("Go to latest"). */
  followCanvasHead: (ref: DeckScopeRef, surfaceId: string) => void;
  openFile: (ref: DeckScopeRef, relativePath: string, line?: number) => void;
  openTerminal: (ref: DeckScopeRef, terminalId: string) => void;
  splitTerminal: (
    ref: DeckScopeRef,
    surfaceId: string,
    terminalId: string,
    direction?: "horizontal" | "vertical",
  ) => void;
  activateTerminal: (ref: DeckScopeRef, surfaceId: string, terminalId: string) => void;
  closeTerminal: (ref: DeckScopeRef, surfaceId: string, terminalId: string) => void;
  activateSurface: (ref: DeckScopeRef, surfaceId: string) => void;
  reorderSurface: (ref: DeckScopeRef, surfaceId: string, overId: string) => void;
  closeSurface: (ref: DeckScopeRef, surfaceId: string) => void;
  closeOtherSurfaces: (ref: DeckScopeRef, surfaceId: string) => void;
  closeSurfacesToRight: (ref: DeckScopeRef, surfaceId: string) => void;
  closeAllSurfaces: (ref: DeckScopeRef) => void;
  reconcileBrowserSurfaces: (ref: DeckScopeRef, tabIds: readonly string[]) => void;
  reconcileFileSurfaces: (ref: DeckScopeRef, workspaceAvailable: boolean) => void;
  show: (ref: DeckScopeRef) => void;
  close: (ref: DeckScopeRef) => void;
  toggleVisibility: (ref: DeckScopeRef) => void;
  toggle: (
    ref: DeckScopeRef,
    kind: Exclude<BoardKind, "file" | "member" | "terminal" | "sub-session" | RetiredBoardKind>,
  ) => void;
  removeThread: (ref: DeckScopeRef) => void;
  /** RM9: seat World session decks read through the view-prefs door (World-held view state). */
  hydrateSessionDecks: (entries: ReadonlyArray<readonly [string, ThreadBoardDeckState]>) => void;
}

const EMPTY_THREAD_STATE: ThreadBoardDeckState = {
  isOpen: false,
  activeSurfaceId: null,
  surfaces: [],
};

const singletonSurface = (kind: SingletonBoardKind): Board => {
  switch (kind) {
    case "goals":
      return { id: "goals", kind };
    case "files":
      return { id: "files", kind };
    case "agents":
      return { id: "agents", kind };
    case "settings":
      return { id: "settings", kind };
    case "usage":
      return { id: "usage", kind };
    case "music":
      return { id: "music", kind };
    case "boomgik":
      return { id: "boomgik", kind };
    case "calendar":
      return { id: "calendar", kind };
    case "inbox":
      return { id: "inbox", kind };
    case "commerce":
      return { id: "commerce", kind };
    case "world-commerce":
      return { id: "world-commerce", kind };
    case "world-members":
      return { id: "world-members", kind };
    case "models":
      return { id: "models", kind };
    case "kanban":
      return { id: "kanban", kind };
    case "workrooms":
      return { id: "workrooms", kind };
    case "designer":
      return { id: "designer", kind };
    case "media":
      return { id: "media", kind };
    case "websites":
      return { id: "websites", kind };
    case "crm":
      return { id: "crm", kind };
    case "games":
      return { id: "games", kind };
    case "delegation-gates":
      return { id: "delegation-gates", kind };
    case "platform-admin":
      return { id: "platform-admin", kind };
    case "mobile-app":
      return { id: "mobile-app", kind };
    case "components":
      return { id: "components", kind };
  }
};

const browserSurface = (tabId: string | null): Board =>
  tabId
    ? { id: `browser:${tabId}`, kind: "preview", resourceId: tabId }
    : { id: "browser:new", kind: "preview", resourceId: null };

const fileSurface = (
  relativePath: string,
  revealLine: number | null,
  revealRequestId: number,
): Board => ({
  id: `file:${relativePath}`,
  kind: "file",
  relativePath,
  revealLine,
  revealRequestId,
});

const terminalSurface = (terminalId: string): Board => ({
  id: `terminal:${terminalId}`,
  kind: "terminal",
  resourceId: terminalId,
  terminalIds: [terminalId],
  activeTerminalId: terminalId,
});

const upsertSurface = (
  current: ThreadBoardDeckState,
  surface: Board,
  activate = true,
): ThreadBoardDeckState => ({
  ...current,
  isOpen: true,
  surfaces: current.surfaces.some((entry) => entry.id === surface.id)
    ? current.surfaces
    : [...current.surfaces, surface],
  activeSurfaceId: activate ? surface.id : current.activeSurfaceId,
});

const updateThread = (
  byThreadKey: Record<string, ThreadBoardDeckState>,
  threadKey: string,
  updater: (current: ThreadBoardDeckState) => ThreadBoardDeckState,
): Record<string, ThreadBoardDeckState> => {
  const current = byThreadKey[threadKey] ?? EMPTY_THREAD_STATE;
  const next = updater(current);
  if (
    next.canvasInitialized !== true &&
    !next.isOpen &&
    next.activeSurfaceId === null &&
    next.surfaces.length === 0
  ) {
    if (!(threadKey in byThreadKey)) return byThreadKey;
    const { [threadKey]: _removed, ...rest } = byThreadKey;
    return rest;
  }
  if (next === current) return byThreadKey;
  return { ...byThreadKey, [threadKey]: next };
};

function normalizeRevealLine(line: number | undefined): number | null {
  if (line === undefined || !Number.isFinite(line)) return null;
  return Math.max(1, Math.trunc(line));
}

/** A restored target must carry one consistent project identity. */
export function validBoomgikBoard(value: Record<string, unknown>): boolean {
  if (value["kind"] !== "boomgik") return false;
  if (value["id"] === "boomgik") return value["projectId"] === undefined;
  const projectId = value["projectId"];
  return (
    isMusicProjectId(projectId) &&
    value["id"] === `boomgik:${projectId}` &&
    typeof value["label"] === "string" &&
    value["label"].length <= 120
  );
}

export function migratePersistedBoardDeckState(persistedState: unknown): {
  byThreadKey: Record<string, ThreadBoardDeckState>;
} {
  if (!persistedState || typeof persistedState !== "object") {
    return { byThreadKey: {} };
  }
  const byThreadKey =
    "byThreadKey" in persistedState &&
    persistedState.byThreadKey &&
    typeof persistedState.byThreadKey === "object"
      ? Object.fromEntries(
          Object.entries(persistedState.byThreadKey as Record<string, ThreadBoardDeckState>)
            .filter(([threadKey]) => !isPullRequestsPanelKey(threadKey))
            .map(([threadKey, threadState]) => {
              const validThreadState =
                threadState && typeof threadState === "object" ? threadState : null;
              const surfaces = Array.isArray(validThreadState?.surfaces)
                ? validThreadState.surfaces.flatMap<Board>((surface) => {
                    // Dropped surface kind: plans now render inline in the
                    // transcript (v9).
                    if ((surface as { kind?: string }).kind === "plan") return [];
                    // Retired with hosted source control (v15) and the Desk's Git features (v16).
                    if (isRetiredBoardKind((surface as { kind?: string }).kind)) return [];
                    // NPA3 crosswalk (v13): the retired Cleo Charter and Tools
                    // tabs open the console at their register.
                    const retiredKind = (surface as { kind?: string }).kind;
                    if (retiredKind === "skill-rooms")
                      return [{ id: "workrooms", kind: "workrooms" }];
                    if (retiredKind === "charter" || retiredKind === "tools")
                      return [
                        { id: "platform-admin", kind: "platform-admin", section: retiredKind },
                      ];
                    if (surface.kind === "boomgik")
                      return validBoomgikBoard(surface) ? [surface] : [];
                    if (surface.kind === "file") {
                      const revealLine =
                        typeof surface.revealLine === "number" &&
                        Number.isFinite(surface.revealLine)
                          ? Math.max(1, Math.trunc(surface.revealLine))
                          : null;
                      const revealRequestId =
                        typeof surface.revealRequestId === "number" &&
                        Number.isSafeInteger(surface.revealRequestId) &&
                        surface.revealRequestId >= 0
                          ? surface.revealRequestId
                          : 0;
                      return [{ ...surface, revealLine, revealRequestId }];
                    }
                    if (surface.kind !== "terminal") return [surface];
                    if (
                      !("resourceId" in surface) ||
                      typeof surface.resourceId !== "string" ||
                      surface.id !== `terminal:${surface.resourceId}`
                    ) {
                      return [];
                    }
                    const terminalIds =
                      "terminalIds" in surface && Array.isArray(surface.terminalIds)
                        ? [
                            ...new Set(
                              surface.terminalIds.filter(
                                (terminalId): terminalId is string =>
                                  typeof terminalId === "string",
                              ),
                            ),
                          ]
                        : [surface.resourceId];
                    const activeTerminalId =
                      "activeTerminalId" in surface &&
                      typeof surface.activeTerminalId === "string" &&
                      terminalIds.includes(surface.activeTerminalId)
                        ? surface.activeTerminalId
                        : (terminalIds[0] ?? surface.resourceId);
                    return [
                      {
                        ...surface,
                        terminalIds: terminalIds.length > 0 ? terminalIds : [surface.resourceId],
                        activeTerminalId,
                      },
                    ];
                  })
                : [];
              // One console and one Workrooms tab per deck: crosswalks can
              // mint the same singleton twice; the first keeps its state.
              const seen = new Set<string>();
              const surfacesOnce = surfaces.filter((surface) => {
                if (surface.kind !== "platform-admin" && surface.kind !== "workrooms") return true;
                if (seen.has(surface.id)) return false;
                seen.add(surface.id);
                return true;
              });
              const rawActiveSurfaceId =
                validThreadState?.activeSurfaceId === "charter" ||
                validThreadState?.activeSurfaceId === "tools"
                  ? "platform-admin"
                  : typeof validThreadState?.activeSurfaceId === "string"
                    ? normalizeStoredWorkroomBoardAddress(validThreadState.activeSurfaceId)
                    : validThreadState?.activeSurfaceId;
              const persistedActiveSurfaceId = surfacesOnce.some(
                (surface) => surface.id === rawActiveSurfaceId,
              )
                ? (rawActiveSurfaceId ?? null)
                : null;
              // A migration that dropped every surface (e.g. plan-only panels
              // in v9) must not reopen an empty panel.
              const isOpen =
                surfacesOnce.length > 0 &&
                (typeof validThreadState?.isOpen === "boolean"
                  ? validThreadState.isOpen
                  : persistedActiveSurfaceId !== null);
              // An open panel needs an active surface: if migration dropped
              // the persisted one (e.g. plan was active), fall back to the
              // first survivor instead of rendering an open empty panel.
              const activeSurfaceId =
                persistedActiveSurfaceId ?? (isOpen ? (surfacesOnce[0]?.id ?? null) : null);
              return [
                threadKey,
                {
                  isOpen,
                  surfaces: surfacesOnce,
                  activeSurfaceId,
                  ...(validThreadState?.canvasInitialized === true
                    ? { canvasInitialized: true as const }
                    : {}),
                },
              ];
            }),
        )
      : {};
  return { byThreadKey };
}

export const useBoardDeckStore = create<WorkbenchDeckStoreState>()(
  persist(
    (set) => ({
      byThreadKey: {},
      ensureDeckDefaultSurface: (ref, defaultKind = "goals") =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (defaultKind === null)
              return current.canvasInitialized === true
                ? current
                : { ...current, canvasInitialized: true, isOpen: true };
            if (defaultKind !== "goals") {
              // A board-born thread: its body is the default and must never
              // be the Goals surface (SB-ANTI-05). Present already → leave the
              // deck alone; absent → seat and front it, even on a deck that an
              // earlier render initialized with Goals.
              const boardSurface = singletonSurface(defaultKind);
              const present = current.surfaces.some((surface) => surface.id === boardSurface.id);
              if (present && current.canvasInitialized === true) return current;
              return {
                ...(present ? current : upsertSurface(current, boardSurface)),
                canvasInitialized: true,
                isOpen: true,
              };
            }
            if (current.canvasInitialized === true) return current;
            const agentSurface = singletonSurface("goals");
            return {
              ...current,
              canvasInitialized: true,
              isOpen: true,
              activeSurfaceId: agentSurface.id,
              surfaces: [
                agentSurface,
                ...current.surfaces.filter((surface) => surface.id !== agentSurface.id),
              ],
            };
          }),
        })),
      open: (ref, kind) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (kind === "preview") {
              const existing = current.surfaces.find((surface) => surface.kind === "preview");
              return upsertSurface(current, existing ?? browserSurface(null));
            }
            return upsertSurface(current, singletonSurface(kind));
          }),
        })),
      openBoardSurfaceAtBirth: (ref, kind) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...upsertSurface(current, singletonSurface(kind)),
            canvasInitialized: true,
            isOpen: true,
          })),
        })),
      seatSession: (ref, sessionId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (
              current.surfaces.some(
                (entry) => entry.kind === "sub-session" && entry.sessionId === sessionId,
              )
            )
              return current;
            // A member-door seat has an opaque id; preserve it by identity.
            // If its id happens to resemble this child's generated one,
            // allocate another seat id without changing either child target.
            let id: `sub-session:${string}` = `sub-session:${sessionId}`;
            let suffix = 1;
            while (current.surfaces.some((entry) => entry.id === id))
              id = `sub-session:${sessionId}:${suffix++}`;
            return {
              ...current,
              surfaces: [...current.surfaces, { id, kind: "sub-session", sessionId }],
            };
          }),
        })),
      openBoomgikProject: (ref, projectId, label) => {
        decodeMusicProjectId(projectId);
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            upsertSurface(current, {
              id: `boomgik:${projectId}`,
              kind: "boomgik",
              projectId,
              label: label.slice(0, 120),
            }),
          ),
        }));
      },
      updateBoomgikTitle: (ref, boardId, label) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const held = current.surfaces.find((board) => board.id === boardId);
            if (held?.kind !== "boomgik" || !("projectId" in held) || held.label === label)
              return current;
            return {
              ...current,
              surfaces: current.surfaces.map((board) =>
                board === held ? { ...held, label: label.slice(0, 120) } : board,
              ),
            };
          }),
        })),
      updateMemberLabel: (ref, surfaceId, label) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const held = current.surfaces.find((board) => board.id === surfaceId);
            const next = label.trim().slice(0, 120);
            if (held?.kind !== "member" || next === "" || held.label === next) return current;
            return {
              ...current,
              surfaces: current.surfaces.map((board) =>
                board === held ? { ...held, label: next } : board,
              ),
            };
          }),
        })),
      openBrowser: (ref, tabId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = browserSurface(tabId);
            const withoutPlaceholder = tabId
              ? current.surfaces.filter((entry) => entry.id !== "browser:new")
              : current.surfaces;
            return upsertSurface({ ...current, surfaces: withoutPlaceholder }, surface);
          }),
        })),
      openFile: (ref, relativePath, line) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const withoutStandaloneExplorer = current.surfaces.filter(
              (surface) => surface.kind !== "files",
            );
            const surfaceId = `file:${relativePath}` as const;
            const existing = withoutStandaloneExplorer.find(
              (surface): surface is Extract<Board, { kind: "file" }> =>
                surface.id === surfaceId && surface.kind === "file",
            );
            const surface = fileSurface(
              relativePath,
              normalizeRevealLine(line),
              (existing?.revealRequestId ?? 0) + 1,
            );
            return {
              ...current,
              isOpen: true,
              activeSurfaceId: surface.id,
              surfaces: existing
                ? withoutStandaloneExplorer.map((entry) =>
                    entry.id === surface.id ? surface : entry,
                  )
                : [...withoutStandaloneExplorer, surface],
            };
          }),
        })),
      openMember: (ref, member) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surfaceId = `member:${member.memberId}` as const;
            const existing = current.surfaces.find((entry) => entry.id === surfaceId);
            // Singleton reopen (D-CT3 approval): the second open FRONTS the
            // standing tab and keeps its pinned revision — never a reset.
            if (existing !== undefined) {
              // Selecting a saved tool result is an explicit revision choice.
              // Ordinary member reopens still keep the standing tab's revision.
              return {
                ...current,
                isOpen: true,
                activeSurfaceId: surfaceId,
                ...(member.canvasRevision && existing.kind === "member"
                  ? {
                      surfaces: current.surfaces.map((entry) =>
                        entry === existing
                          ? { ...existing, canvasRevision: member.canvasRevision! }
                          : entry,
                      ),
                    }
                  : {}),
              };
            }
            return upsertSurface(current, {
              id: surfaceId,
              kind: "member",
              memberId: member.memberId,
              memberType: member.memberType,
              worldKey: member.worldKey,
              label: member.label,
              pinnedRevisionKey: null,
              ...(member.canvasRevision ? { canvasRevision: member.canvasRevision } : {}),
            });
          }),
        })),
      pinMemberRevision: (ref, surfaceId, revisionKey) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...current,
            surfaces: current.surfaces.map((entry) =>
              entry.id === surfaceId && entry.kind === "member"
                ? { ...entry, pinnedRevisionKey: revisionKey }
                : entry,
            ),
          })),
        })),
      followCanvasHead: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...current,
            surfaces: current.surfaces.map((entry) => {
              if (entry.id !== surfaceId || entry.kind !== "member" || !entry.canvasRevision)
                return entry;
              const { canvasRevision: _followed, ...following } = entry;
              return following;
            }),
          })),
        })),
      openTerminal: (ref, terminalId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            upsertSurface(current, terminalSurface(terminalId)),
          ),
        })),
      splitTerminal: (ref, surfaceId, terminalId, direction = "horizontal") =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...current,
            isOpen: true,
            activeSurfaceId: surfaceId,
            surfaces: current.surfaces.map((surface) => {
              if (surface.id !== surfaceId || surface.kind !== "terminal") return surface;
              const { splitDirection: _splitDirection, ...baseSurface } = surface;
              return {
                ...baseSurface,
                terminalIds: surface.terminalIds.includes(terminalId)
                  ? surface.terminalIds
                  : [...surface.terminalIds, terminalId],
                activeTerminalId: terminalId,
                ...(direction === "vertical" ? { splitDirection: "vertical" as const } : {}),
              };
            }),
          })),
        })),
      activateTerminal: (ref, surfaceId, terminalId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...current,
            activeSurfaceId: surfaceId,
            surfaces: current.surfaces.map((surface) =>
              surface.id === surfaceId &&
              surface.kind === "terminal" &&
              surface.terminalIds.includes(terminalId)
                ? { ...surface, activeTerminalId: terminalId }
                : surface,
            ),
          })),
        })),
      closeTerminal: (ref, surfaceId, terminalId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = current.surfaces.find(
              (entry) => entry.id === surfaceId && entry.kind === "terminal",
            );
            if (!surface || surface.kind !== "terminal") return current;
            const terminalIds = surface.terminalIds.filter((id) => id !== terminalId);
            if (terminalIds.length === 0) {
              const index = current.surfaces.findIndex((entry) => entry.id === surfaceId);
              const surfaces = current.surfaces.filter((entry) => entry.id !== surfaceId);
              const fallback = surfaces[Math.min(index, surfaces.length - 1)] ?? null;
              return {
                ...current,
                isOpen: surfaces.length > 0 && current.isOpen,
                surfaces,
                activeSurfaceId:
                  current.activeSurfaceId === surfaceId
                    ? (fallback?.id ?? null)
                    : current.activeSurfaceId,
              };
            }
            return {
              ...current,
              surfaces: current.surfaces.map((entry) =>
                entry.id === surfaceId && entry.kind === "terminal"
                  ? {
                      ...entry,
                      terminalIds,
                      activeTerminalId:
                        entry.activeTerminalId === terminalId
                          ? (terminalIds.at(-1) ?? terminalIds[0]!)
                          : entry.activeTerminalId,
                    }
                  : entry,
              ),
            };
          }),
        })),
      activateSurface: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            current.surfaces.some((surface) => surface.id === surfaceId)
              ? { ...current, isOpen: true, activeSurfaceId: surfaceId }
              : current,
          ),
        })),
      reorderSurface: (ref, surfaceId, overId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const movable = current.surfaces.filter((surface) => surface.kind !== "inbox");
            const from = movable.findIndex((surface) => surface.id === surfaceId);
            const to = movable.findIndex((surface) => surface.id === overId);
            if (from < 0 || to < 0 || from === to) return current;
            const [moved] = movable.splice(from, 1);
            movable.splice(to, 0, moved!);
            let index = 0;
            return {
              ...current,
              surfaces: current.surfaces.map((surface) =>
                surface.kind === "inbox" ? surface : movable[index++]!,
              ),
            };
          }),
        })),
      closeSurface: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const index = current.surfaces.findIndex((surface) => surface.id === surfaceId);
            if (index < 0) return current;
            const surfaces = current.surfaces.filter((surface) => surface.id !== surfaceId);
            if (current.activeSurfaceId !== surfaceId) {
              return { ...current, isOpen: surfaces.length > 0 && current.isOpen, surfaces };
            }
            // The deck's active tab is a surface concept: a session seat is
            // navigation into a child room, never this room's content, so the
            // fallback walks left past session seats (§8 one-bar room).
            const at = Math.min(index, surfaces.length - 1);
            const fallback =
              surfaces[at]?.kind !== "sub-session"
                ? (surfaces[at] ?? null)
                : (surfaces
                    .slice(0, at)
                    .reverse()
                    .find((entry) => entry.kind !== "sub-session") ??
                  surfaces.find((entry) => entry.kind !== "sub-session") ??
                  null);
            return {
              ...current,
              isOpen: surfaces.length > 0 && current.isOpen,
              surfaces,
              activeSurfaceId: fallback?.id ?? null,
            };
          }),
        })),
      // The bulk close verbs govern SURFACES only: a session seat survives
      // every one of them, because a session leaves the row solely through
      // its own engagement ceremony (§8 — merged in presentation, never in
      // meaning).
      closeOtherSurfaces: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = current.surfaces.find((entry) => entry.id === surfaceId);
            if (!surface || current.surfaces.length === 1) return current;
            return {
              ...current,
              isOpen: true,
              surfaces: current.surfaces.filter(
                (entry) => entry.id === surfaceId || entry.kind === "sub-session",
              ),
              activeSurfaceId: surface.id,
            };
          }),
        })),
      closeSurfacesToRight: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const index = current.surfaces.findIndex((surface) => surface.id === surfaceId);
            if (index < 0 || index === current.surfaces.length - 1) return current;
            const surfaces = current.surfaces.filter(
              (surface, position) => position <= index || surface.kind === "sub-session",
            );
            if (surfaces.length === current.surfaces.length) return current;
            const activeStillExists = surfaces.some(
              (surface) => surface.id === current.activeSurfaceId,
            );
            return {
              ...current,
              surfaces,
              activeSurfaceId: activeStillExists ? current.activeSurfaceId : surfaceId,
            };
          }),
        })),
      closeAllSurfaces: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surfaces = current.surfaces.filter((surface) => surface.kind === "sub-session");
            if (surfaces.length === current.surfaces.length && current.activeSurfaceId === null) {
              return current;
            }
            return { ...current, isOpen: false, surfaces, activeSurfaceId: null };
          }),
        })),
      reconcileBrowserSurfaces: (ref, tabIds) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const validIds = new Set(tabIds.map((tabId) => `browser:${tabId}`));
            const nonBrowser = current.surfaces.filter((surface) => surface.kind !== "preview");
            const existingBrowser = current.surfaces.filter(
              (surface): surface is Extract<Board, { kind: "preview" }> =>
                surface.kind === "preview" &&
                surface.id !== "browser:new" &&
                (validIds.has(surface.id) || nativePreviewRunId(surface.resourceId) !== null),
            );
            const knownIds = new Set(existingBrowser.map((surface) => surface.id));
            const added = tabIds
              .filter((tabId) => !knownIds.has(`browser:${tabId}`))
              .map((tabId) => browserSurface(tabId));
            const surfaces = [...nonBrowser, ...existingBrowser, ...added];
            const activeStillExists = surfaces.some(
              (surface) => surface.id === current.activeSurfaceId,
            );
            const fallbackBrowser = surfaces.find((surface) => surface.kind === "preview");
            const activeSurfaceId = activeStillExists
              ? current.activeSurfaceId
              : (fallbackBrowser?.id ?? surfaces[0]?.id ?? null);
            // Nothing to reconcile: the same boards in the same order and the
            // same fronted one are the same deck, so subscribers stay quiet.
            if (
              activeSurfaceId === current.activeSurfaceId &&
              surfaces.length === current.surfaces.length &&
              surfaces.every((surface, index) => surface === current.surfaces[index])
            ) {
              return current;
            }
            return { ...current, surfaces, activeSurfaceId };
          }),
        })),
      reconcileFileSurfaces: (ref, workspaceAvailable) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (workspaceAvailable) return current;
            const surfaces = current.surfaces.filter(
              (surface) => surface.kind !== "files" && surface.kind !== "file",
            );
            if (surfaces.length === current.surfaces.length) return current;
            const activeStillExists = surfaces.some(
              (surface) => surface.id === current.activeSurfaceId,
            );
            return {
              ...current,
              isOpen: surfaces.length > 0 ? current.isOpen : false,
              surfaces,
              activeSurfaceId: activeStillExists
                ? current.activeSurfaceId
                : (surfaces.at(-1)?.id ?? null),
            };
          }),
        })),
      show: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            current.isOpen ? current : { ...current, isOpen: true },
          ),
        })),
      close: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            current.isOpen ? { ...current, isOpen: false } : current,
          ),
        })),
      toggleVisibility: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...current,
            isOpen: !current.isOpen,
          })),
        })),
      toggle: (ref, kind) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const active = current.surfaces.find(
              (surface) => surface.id === current.activeSurfaceId,
            );
            if (current.isOpen && active?.kind === kind) {
              return { ...current, isOpen: false };
            }
            if (kind === "preview") {
              const existing = current.surfaces.find((surface) => surface.kind === "preview");
              return upsertSurface(current, existing ?? browserSurface(null));
            }
            return upsertSurface(current, singletonSurface(kind));
          }),
        })),
      hydrateSessionDecks: (entries) =>
        set((state) => {
          if (entries.length === 0) return state;
          const byThreadKey = { ...state.byThreadKey };
          for (const [threadKey, deck] of entries) byThreadKey[threadKey] = deck;
          return { byThreadKey };
        }),
      removeThread: (ref) =>
        set((state) => {
          const threadKey = scopedThreadKey(ref);
          if (!(threadKey in state.byThreadKey)) return state;
          const { [threadKey]: _removed, ...rest } = state.byThreadKey;
          return { byThreadKey: rest };
        }),
    }),
    {
      name: RIGHT_PANEL_STORAGE_KEY,
      version: RIGHT_PANEL_STORAGE_VERSION,
      storage: createJSONStorage(() =>
        resolveStorage(typeof window !== "undefined" ? window.localStorage : undefined),
      ),
      // RM9: a World session's deck is World-held view state (the view-prefs
      // door); browser storage keeps only Local decks and per-device conveniences.
      partialize: (state) => ({
        byThreadKey: Object.fromEntries(
          Object.entries(state.byThreadKey).filter(
            ([threadKey]) => !threadKey.startsWith("world-session:"),
          ),
        ),
      }),
      migrate: migratePersistedBoardDeckState,
    },
  ),
);

export function selectThreadBoardDeckState(
  byThreadKey: Record<string, ThreadBoardDeckState>,
  ref: DeckScopeRef | null | undefined,
): ThreadBoardDeckState {
  if (!ref) return EMPTY_THREAD_STATE;
  return byThreadKey[scopedThreadKey(ref)] ?? EMPTY_THREAD_STATE;
}

export function selectActiveBoardDeck(
  byThreadKey: Record<string, ThreadBoardDeckState>,
  ref: DeckScopeRef | null | undefined,
): BoardKind | null {
  const state = selectThreadBoardDeckState(byThreadKey, ref);
  if (!state.isOpen) return null;
  return state.surfaces.find((surface) => surface.id === state.activeSurfaceId)?.kind ?? null;
}

export function selectActiveDeckSurface(
  byThreadKey: Record<string, ThreadBoardDeckState>,
  ref: DeckScopeRef | null | undefined,
): Board | null {
  const state = selectThreadBoardDeckState(byThreadKey, ref);
  if (!state.isOpen) return null;
  return selectSelectedDeckSurface(byThreadKey, ref);
}

/** The selected surface even while the panel is hidden, so a layout control can restore it. */
export function selectSelectedDeckSurface(
  byThreadKey: Record<string, ThreadBoardDeckState>,
  ref: DeckScopeRef | null | undefined,
): Board | null {
  const state = selectThreadBoardDeckState(byThreadKey, ref);
  return state.surfaces.find((surface) => surface.id === state.activeSurfaceId) ?? null;
}
